// A7: ortak aday kartı — OrganizerSearch, Organizatör bul ve Meko sohbet kartları aynı backend kartını
// (`facts`, `reasons`, `availability`, `cta`) aynı biçimde gösterir. Numerik skor/yüzde hiçbir varyantta yoktur.
// Kayıtlı kartta teklif CTA'sını ekran verir (`actions`); dış kartta CTA her zaman doğrulanmış herkese açık bağlantıdır,
// uygulama içi teklif düğmesi gösterilmez (EXT-08).
import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Users, ExternalLink } from 'lucide-react';
import { FeaturedBadge, FounderBadge } from './PlanBadges';
import { capacityText } from '../lib/capacity';
import { apiErrorMessage } from '../lib/apiError';
import { districtSummary } from '../lib/districtSummary';
import { EVENT_TYPE_LABELS } from '../constants/canonicalEnums';
import './CandidateCard.css';

export const AVAILABILITY_OUTSIDE = 'Müsaitlik dışı teklif';

export const candidateId = card => card?.id || card?.organizer?.id || card?.project_id || '';
export const isExternalCandidate = card => card?.kind === 'external' || card?.is_external === true || card?.external === true;

// Doğrulanmış herkese açık bağlantı yalnız https olabilir; başka şema (javascript:, data:) asla bağlantı olmaz.
export const safePublicUrl = url => (typeof url === 'string' && /^https:\/\/[^\s]+$/i.test(url) ? url : null);

const list = value => (Array.isArray(value) ? value.filter(Boolean) : []);

export const candidateDistrict = card => {
  const district = card?.district || card?.semt;
  if (district) return card?.mahalle ? `${districtSummary(district)} · ${card.mahalle}` : districtSummary(district);
  return districtSummary(list(card?.district_labels)) || districtSummary(list(card?.brief?.preferred_districts))
    || districtSummary(list(card?.organizer?.discovery_districts));
};

export const candidateCapacity = card => {
  if (card?.capacity?.text) return card.capacity.text;
  if (card?.max_capacity != null) return capacityText(card.min_capacity, card.max_capacity);
  const org = card?.organizer;
  if (org?.typical_attendees_max != null) return capacityText(null, org.typical_attendees_max);
  return 'Net değil';
};

export const candidateTypes = card => {
  if (list(card?.event_type_labels).length) return list(card.event_type_labels);
  const raw = list(card?.accepted_event_types).length ? card.accepted_event_types : list(card?.organizer?.discovery_event_types);
  return raw.map(t => EVENT_TYPE_LABELS[t] || t);
};

// 1–2 kanıt satırı: yalnız backend'in `reasons` ile işaret ettiği `facts`. Eski (A7 öncesi) kartta okunur gerekçe.
export const evidenceLines = (card, limit = 2) => {
  const facts = list(card?.facts);
  if (facts.length) {
    const byId = Object.fromEntries(facts.map(f => [f.id, f]));
    return list(card.reasons).map(id => byId[id]).filter(Boolean).slice(0, limit).map(f => `${f.label}: ${f.value}`);
  }
  return list(card?.reasons_positive?.length ? card.reasons_positive : card?.confirmed).slice(0, limit);
};

export default function CandidateCard({
  card, as: Tag = 'article', className = '', testId, heading: Heading = 'h3', headingClassName = '', title, eyebrow, media,
  showText = false, actions = null, children = null, onNotThis, onExclude, disabled = false,
}) {
  const id = candidateId(card);
  const external = isExternalCandidate(card);
  const district = candidateDistrict(card);
  const types = candidateTypes(card);
  const evidence = evidenceLines(card);
  const outside = card?.availability === 'outside';
  const url = external ? safePublicUrl(card?.cta?.url) : null;
  const externalLabel = card?.external_label || (card?.entity_type === 'organizer' ? 'Dış Organizatör' : 'Dış Mekân');
  const text = showText ? (card?.text || card?.blurb) : null;
  return <Tag className={`candidate-card ${className}`.trim()} data-testid={testId || `candidate-card-${id}`} data-kind={card?.kind || 'registered'}>
    {media}
    {(external || outside || (!external && (card?.featured || card?.founder_badge?.number))) && <div className="candidate-badges">
      {external && <span className="candidate-badge candidate-badge-external" data-testid={`candidate-external-label-${id}`}>{externalLabel}</span>}
      {/* A6: "Öne Çıkan" yalnız aktif ücretli kayıtlı kartta; kurucu rozeti ayrı ve hak vermez; dış kart rozet almaz. */}
      {!external && card?.featured && <FeaturedBadge />}
      {!external && <FounderBadge founder={card?.founder_badge} />}
      {outside && <span className="candidate-badge candidate-badge-outside" data-testid={`candidate-availability-outside-${id}`}>{card?.availability_label || AVAILABILITY_OUTSIDE}</span>}
    </div>}
    {eyebrow && <p className="candidate-eyebrow">{eyebrow}</p>}
    <Heading className={`candidate-name ${headingClassName}`.trim()}>{title || card?.name}</Heading>
    <p className="candidate-meta">
      <span><MapPin size={14} strokeWidth={1.75} aria-hidden="true" />{district || 'İlçe net değil'}</span>
      <span data-testid={`candidate-capacity-${id}`}><Users size={14} strokeWidth={1.75} aria-hidden="true" />{candidateCapacity(card)}</span>
    </p>
    {types.length > 0 && <ul className="candidate-tags" aria-label="Etkinlik türleri">{types.slice(0, 4).map(t => <li key={t}>{t}</li>)}</ul>}
    {text && <p className="candidate-text" data-testid={`candidate-text-${id}`}>{text}</p>}
    {evidence.length > 0 && <ul className="candidate-evidence" aria-label="Neden önerildi" data-testid={`candidate-facts-${id}`}>{evidence.map(line => <li key={line}>{line}</li>)}</ul>}
    {external && <p className="candidate-note">Potansiyel seçenek; kapasite, fiyat ve uygunluk görüşülmesi gerekir. Uygulama içinden teklif gönderilmez.</p>}
    {children}
    {(external ? url : actions) || onNotThis || onExclude ? <div className="candidate-actions">
      {external ? url && <a className="action-secondary" href={url} target="_blank" rel="noopener noreferrer nofollow" data-testid={`candidate-public-link-${id}`}>{card?.cta?.label || 'Herkese açık bağlantıyı aç'}<ExternalLink size={15} aria-hidden="true" /></a> : actions}
      {onNotThis && <button type="button" className="text-action" disabled={disabled} onClick={() => onNotThis(card)} data-testid={`candidate-not-this-${id}`}>Aradığım bu değil</button>}
      {onExclude && <button type="button" className="text-action" disabled={disabled} onClick={() => onExclude(card)} data-testid={`candidate-exclude-${id}`}>Bunu bana bir daha önerme</button>}
    </div> : null}
  </Tag>;
}

// MATCH-14: "Ekibe ihtiyaç bildir" — yalnız kullanıcı basınca, sunucunun imzaladığı arama özetiyle (need_token).
// Serbest metin, iletişim bilgisi veya kriter istemciden gönderilmez; aynı ihtiyaç 24 saat içinde sayaç artırır.
export function NeedReportButton({ token, testId = 'need-report', className = 'action-secondary' }) {
  const [state, setState] = useState({ status: 'idle', created: true, error: '' });
  if (!token) return null;
  if (state.status === 'done') return <p role="status" data-testid={`${testId}-done`}>{state.created
    ? 'İhtiyacını havuz ekibine ilettik. Uygun bir profil eklenince aynı aramayı yeniden yapabilirsin.'
    : 'Bu ihtiyacı daha önce iletmiştin; ekibe yeniden hatırlattık.'}</p>;
  const send = async () => {
    setState({ status: 'busy', created: true, error: '' });
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL || ''}/api/matchmaking/needs`, { need_token: token }, { timeout: 15000 });
      setState({ status: 'done', created: data?.created !== false, error: '' });
    } catch (e) {
      setState({ status: 'idle', created: true, error: apiErrorMessage(e, 'İhtiyacı şu an iletemedik; biraz sonra yeniden dene.') });
    }
  };
  return <>
    <button type="button" className={className} disabled={state.status === 'busy'} onClick={send} data-testid={testId}>{state.status === 'busy' ? 'İletiliyor…' : 'Ekibe ihtiyaç bildir'}</button>
    {state.error && <p role="alert" data-testid={`${testId}-error`}>{state.error}</p>}
  </>;
}

// 0 sonuç (MATCH-14): en çok eleyen sert koşullar (yalnız sayılar, kişi bilgisi yok) + onaylı genişletme düğmeleri.
// Öneri yalnız kullanıcı düğmeye bastığında uygulanır; ekran kendiliğinden genişletmez.
export function CandidateZero({ zero, onWiden, onReportNeed, disabled = false, testId = 'search-zero', subject = 'mekân' }) {
  if (!zero) return null;
  const reasons = list(zero.reasons);
  const suggestions = list(zero.suggestions);
  const prefix = testId.replace(/-zero$/, '');
  return <section className="candidate-zero" data-testid={testId} aria-label="Sonuç çıkmadı">
    <h3>Bu koşullara uyan {subject} çıkmadı</h3>
    <p>{zero.message || 'Bu koşullara uyan profil şu an yok.'}</p>
    {reasons.length > 0 && <><p>En çok eleyen koşullar:</p><ul>{reasons.map(r => <li key={r.code || r.label} data-testid={`${testId}-reason`}>{r.label}: {r.count} profil</li>)}</ul></>}
    {suggestions.length > 0 && <><p>İstersen aramayı şöyle genişletebilirim:</p><div className="candidate-actions">
      {suggestions.map(s => <button type="button" key={s.id + s.label} className="action-secondary" disabled={disabled} onClick={() => onWiden(s)} data-testid={`${prefix}-widen-${s.id}`}>{s.label}{s.count ? ` · ${s.count} kayıt` : ''}</button>)}
    </div><p className="candidate-note">Düğmeye bastığında yalnız bu öneri uygulanır ve yeni arama yapılır; arama kendiliğinden genişlemez.</p></>}
    <div className="candidate-actions">{onReportNeed
      ? zero.can_report_need && <button type="button" className="action-secondary" disabled={disabled} onClick={onReportNeed} data-testid={`${prefix}-report-need`}>Ekibe ihtiyaç bildir</button>
      : <NeedReportButton token={zero.need_token} testId={`${prefix}-report-need`} />}</div>
  </section>;
}
